import * as Contract from '@tableau/api-internal-contract-js';
import {
  CommandMessage,
  CreateVersionConverter,
  ErrorCodes,
  ExecuteParameters,
  ExecuteResponse,
  InternalContractVersionConverter,
  INTERNAL_CONTRACT_VERSION,
  Messenger,
  VerbId,
} from '@tableau/api-internal-contract-js';
import * as PresLayerContract from '@tableau/preslayer-api-contract';
import { ApiContext } from '../ApiContext';
import { AddInInstancePresModel, ApiEventHandler } from '../ApiCore';
import { Disposable } from '../Disposable';
import { DesktopUINamespaceHandler } from '../desktop-bootstrap/DesktopUINamespaceHandler';
import { ServerUINamespaceHandler } from '../server-bootstrap/ServerUINamespaceHandler';
import { telemetryFunctionComplete } from '../telemetry/ExtensionsTelemetryDispatcher';
import { FunctionResults, telemetryFunctionStart } from '../telemetry/TelemetryDispatcher';
import { ApiUtils } from './ApiUtils';
import { FontHandler } from './FontHandler';
import { UINamespaceHandler, UINamespaceOptions } from './UINamespaceHandler';

/**
 * This class deals with marshalling info from the presentation layer
 * to the frame which is using the API.
 */
export class PresLayerHandler implements Disposable {
  private _apiEventHandler: ApiEventHandler;
  private _fontHandler: FontHandler;
  private _uiNamespaceHandler: UINamespaceHandler;
  private _versionConverter: InternalContractVersionConverter;

  /**
   * Creates an instance of PresLayerHandler.
   * @param presLayer The instantiated pres layer for this class to use
   * @param extensionLocator Locator with info about the extension
   * @param versionNumber The version of the Internal API which the extension is using
   * @param _messenger An instantiated Messenger object for us to use
   * @param _uiNamespaceOptions An object containing properties needed for opening/closing a UINamespace dialog
   */
  public constructor(
    presLayer: PresLayerContract.PresentationLayer,
    private extensionInstance: AddInInstancePresModel,
    loadedExtensionUrl: string,
    versionNumber: Contract.VersionNumber,
    private _messenger: Messenger,
    private _uiNamespaceOptions: UINamespaceOptions = {},
  ) {
    if (!presLayer) {
      throw 'Missing presentation layer';
    }

    if (!extensionInstance.addInLocatorPresModel) {
      throw 'Missing extensions pres model';
    }

    if (!versionNumber) {
      throw 'Missing version number';
    }

    if (!this._messenger) {
      throw 'Missing messenger';
    }

    this._versionConverter = CreateVersionConverter(versionNumber, INTERNAL_CONTRACT_VERSION);

    // The ApiEventHandler is going to do most of the heavy lifting. This class is shared
    // between server and desktop and knows how to use the PresLayer
    this._apiEventHandler = new ApiEventHandler(presLayer, extensionInstance);
    this._apiEventHandler.initialize();
    this._apiEventHandler.setEventHandler(this.onNotification.bind(this));

    // We want to listen for incoming commands from the extension
    this._messenger.setCommandMessageHandler(this.onCommandMessage.bind(this));
    this._messenger.startListening();

    if (ApiContext.getInstance().isServer()) {
      this._uiNamespaceHandler = new ServerUINamespaceHandler(
        presLayer,
        extensionInstance,
        loadedExtensionUrl,
        _messenger,
        this._uiNamespaceOptions.closeNotificationCallback,
      );
    } else {
      this._uiNamespaceHandler = new DesktopUINamespaceHandler();
    }

    this._fontHandler = new FontHandler(extensionInstance);
  }

  private onCommandMessage(message: CommandMessage, source: Window): void {
    const upgradedParams = this._versionConverter.upgradeExecuteCall(message.verbId, message.parameters);
    const msg: CommandMessage = {
      verbId: upgradedParams.verb,
      parameters: upgradedParams.parameters,
      msgGuid: message.msgGuid,
      msgType: message.msgType,
    };

    // The UINamespace feature hijacks the initializeExtension command to pass initial state to
    // an extension running in a dialog.  The payload is passed and immediately returned from the command.
    if (msg.verbId === Contract.VerbId.InitializeExtension) {
      msg.parameters[Contract.ParameterId.ExtensionDialogPayload] = this._uiNamespaceOptions.initialPayload || '';
    }

    // There are currently two commands that are special cased for handling on server.  Both of these
    // commands involve the UINamespace.  Since the functionality needed is to just open and close a
    // popup window, this is handled directly in the JavaScript, rather than passing the command
    // down to the platform.  Both of these special cases are handled in the ServerUINamespaceHandler class.
    let commandHandled = this._uiNamespaceHandler.tryHandleCommand(msg);

    if (!commandHandled) {
      // Fonts are a special case where all of the data is already on server, so we don't need to pass
      // a command down to the platform.
      const response = this._fontHandler.tryHandleCommand(msg);
      if (response) {
        commandHandled = true;
        this._messenger.prepareCommandResponseMessage(msg.msgGuid, response.result, undefined).send();
      }
    }

    // For all other commands, handle by passing msg to platform.
    if (!commandHandled) {
      const runtimeData = telemetryFunctionStart(); // this timer is only used in case of exceptions during execute
      try {
        this._apiEventHandler.execute(msg.verbId, msg.parameters).then(
          (result) => {
            const downgradedResponse = this._versionConverter.downgradeExecuteReturn(result);
            this._messenger.prepareCommandResponseMessage(msg.msgGuid, downgradedResponse.result, undefined).send();
          },
          (error) => {
            const errorMessage = ApiUtils.getErrorMessageFromException(error);
            telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Error(errorMessage), this.extensionInstance);
            this._messenger
              .prepareCommandResponseMessage(msg.msgGuid, undefined, { message: errorMessage, errorCode: ErrorCodes.INTERNAL_ERROR })
              .send();
          },
        );
      } catch (error) {
        const errorMessage = ApiUtils.getErrorMessageFromException(error);
        telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Error(errorMessage), this.extensionInstance);
        this._messenger
          .prepareCommandResponseMessage(msg.msgGuid, undefined, { message: errorMessage, errorCode: ErrorCodes.INTERNAL_ERROR })
          .send();
      }
    }
  }

  private onNotification(notification: Contract.NotificationId, model: Contract.Model): void {
    // Downgrade the notification to what the external contract is expecting
    const downgradedNotification = this._versionConverter.downgradeNotification({ notificationId: notification, data: model });

    // When we get a notification from the pres layer, forward it on to the extension
    this._messenger.prepareNotificationMessage(downgradedNotification.notificationId, downgradedNotification.data).send();
  }

  /**
   * Executes the requested Api command by converting the parameters, running the command against the pres-layer,
   * then processing the result back into a format the Api can understand
   *
   * @param {VerbId} verb
   * @param {ExecuteParameters} parameters
   * @returns {Promise<ExecuteResponse>}
   * @memberof ApiEventHandler
   */
  public execute(verb: VerbId, parameters: ExecuteParameters): Promise<ExecuteResponse> {
    return this._apiEventHandler.execute(verb, parameters);
  }

  /**
   * Dispose of anything that is no longer needed and may cause problems to have lingering around once the extension is gone
   */
  public dispose(): void {
    this._messenger.stopListening();
    this._apiEventHandler.dispose();
  }
}
